import { useEffect, useState } from "react";
import { basic2ngGraphData } from "../../../../services/webServices";
import { Card, CardContent, Divider, Typography, Box } from "@mui/material";
import ReactECharts from "echarts-for-react";

const monthShortMap = {
  January: "Jan",
  February: "Feb",
  March: "Mar",
  April: "Apr",
  May: "May",
  June: "Jun",
  July: "Jul",
  August: "Aug",
  September: "Sep",
  October: "Oct",
  November: "Nov",
  December: "Dec",
};

const getMonthRange = () => {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const startDate = new Date(currentYear, currentMonth - 5, 1);

  const startMonthStr = `${months[startDate.getMonth()]} ${startDate.getFullYear()}`;
  const endMonthStr = `${months[currentMonth]} ${currentYear}`;

  return `${startMonthStr} - ${endMonthStr}`;
};

const BasicUserActivityChart = () => {
  const [chartOption, setChartOption] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await basic2ngGraphData();
        const data = response.data.basicDashboardData2;

        const filteredData = data.filter(
          (d) => !["Saturday", "Sunday"].includes(d.month.trim())
        );

        const categories = filteredData.map((item) => {
          const trimmedMonth = item.month.trim();
          return monthShortMap[trimmedMonth] || trimmedMonth;
        });

        const totalUsers = filteredData.map((item) => parseInt(item.total_users));
        const totalServices = filteredData.map((item) =>
          parseInt(item.total_services_used)
        );

        setChartOption({
          tooltip: {
            trigger: "axis",
            backgroundColor: "#fafafaff",
            borderWidth: 0,
            borderColor: "#333",
            textStyle: { color: "#000000" },
            formatter: (params) => {
              return `
                <strong>${params[0].axisValue}</strong><br/>
                Total Users: <strong>${totalUsers[params[0].dataIndex]}</strong><br/>
                Services Used: <strong>${totalServices[params[0].dataIndex]}</strong>
              `;
            },
          },
          legend: {
            data: ["Total Users", "Services Used"],
            bottom: 0,
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "12%",
            top: "5%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            data: categories,
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: {
              interval: 0,
              fontSize: 10,
              rotate: 30,
            },
          },
          yAxis: [{ type: "value", name: "No. of Users" }],
          series: [
            {
              name: "Total Users",
              type: "bar",
              data: totalUsers,
              barWidth: 20,
              itemStyle: { color: "#87CEFA" },
            },
            {
              name: "Services Used",
              type: "line",
              data: totalServices,
              symbol: "circle",
              symbolSize: 8,
              lineStyle: {
                color: "#FFB3B6",
                width: 2,
              },
              itemStyle: {
                color: "#FFB3B6",
              },
            },
          ],
        });
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <Card
      sx={{
        backgroundColor: "#F8F9FF",
        borderRadius: "12px",
        boxShadow: `4px 4px 10px #A4A4A44D, -4px -4px 10px #A4A4A44D`,
        p: { xs: 1.5, md: 2 },
        minHeight: 350,
        maxHeight: { xs: "auto", md: 500 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            User Activity
          </Typography>
        </Box>

        <Divider
          sx={{
            mt: 1,
            mb: 3,
            width: "100%",
            borderColor: "#e0e0e0",
          }}
        />

        <Box
          sx={{
            width: "100%",
            height: { xs: 260, md: 360 },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 0, md: 2 },
          }}
        >
          <ReactECharts
            option={chartOption}
            style={{ height: "100%", width: "100%" }}
            className="chart-container"
          />
        </Box>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {getMonthRange()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BasicUserActivityChart;
